import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import currencyApi from '../apiProvider/currencyapi';
import ShowNotifications from '../utils/notification';
import ReactTableComponent from '../table/ReactTableComponent';
import usePermission from '../hook/usePermission';

const CurrenciesLayer = () => {
    const canAdd = usePermission("currencies", "add");
    const canEdit = usePermission("currencies", "edit");
    const canDelete = usePermission("currencies", "delete");
    const havePermissions = canEdit || canDelete;

    const [currencies, setCurrencies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        country: '',
        code: '',
        symbol: '',
        status: 'Active',
        isDefault: false
    });
    const [editId, setEditId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchCurrencies();
    }, [pageIndex, searchQuery, limit]);

    const fetchCurrencies = async () => {
        setIsLoading(true);
        try {
            const params = {
                page: pageIndex,
                limit: limit,
                search: searchQuery
            };
            const res = await currencyApi.getCurrencies(params);
            if (res.status) {
                setCurrencies(res.response?.data || []);
                setTotal(res.response?.total || 0);
            }
        } catch (error) {
            console.error("Error fetching currencies:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNextPage = () => {
        if ((pageIndex + 1) * limit < total) {
            setPageIndex((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (pageIndex > 0) {
            setPageIndex((prev) => prev - 1);
        }
    };

    const columns = useMemo(() => [
        {
            header: "S.No",
            accessorKey: "index",
            cell: ({ row }) => (pageIndex * limit + row.index + 1).toString(),
        },
        { header: "Country", accessorKey: "country", cell: ({ row }) => row.original.country || 'N/A' },
        { header: "Currency Name", accessorKey: "name" },
        { header: "Symbol", accessorKey: "symbol" },
        { header: "Code", accessorKey: "code" },
        {
            header: "Status",
            accessorKey: "status",
            cell: ({ row }) => (
                <button
                    className={`btn btn-subtle-${row.original.status ? "success" : "danger"} btn-sm`}
                >
                    {row.original.status ? "Active" : "Inactive"}
                </button>
            ),
        },
        ...(havePermissions ? [
            {
                header: "Action",
                accessorKey: "actions",
                cell: ({ row }) => (
                    <div className="d-flex align-items-center gap-10 justify-content-center">
                        {canEdit && (
                            <button
                                type="button"
                                className="bg-success-100 text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleEdit(row.original)}
                            >
                                <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                type="button"
                                className="remove-item-button bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleDelete(row.original._id)}
                            >
                                <Icon
                                    icon="fluent:delete-24-regular"
                                    className="menu-icon"
                                />
                            </button>
                        )}
                    </div>
                ),
            }
        ] : [])
    ], [pageIndex, limit, canEdit, canDelete, havePermissions]);

    const handleInputChange = (e) => {
        const { id, value, type, checked } = e.target;
        setErrors(prev => ({ ...prev, [id]: '' }));
        setFormData(prev => ({ ...prev, [id]: type === 'checkbox' ? checked : value }));
    };

    const resetForm = () => {
        setFormData({
            name: '',
            country: '',
            code: '',
            symbol: '',
            status: 'Active',
            isDefault: false
        });
        setEditId(null);
        setErrors({});
    };

    const handleClose = () => {
        setIsModalOpen(false);
        resetForm();
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.country?.trim()) newErrors.country = 'Country Name is required';
        if (!formData.name?.trim()) newErrors.name = 'Currency Name is required';
        if (!formData.symbol?.trim()) newErrors.symbol = 'Symbol is required';
        if (!formData.code?.trim()) newErrors.code = 'Code is required';
        if (!formData.status) newErrors.status = 'Status is required';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            const payload = {
                ...formData,
                status: formData.status === 'Active'
            };

            let res;
            if (editId) {
                res = await currencyApi.updateCurrency(editId, payload);
            } else {
                res = await currencyApi.addCurrency(payload);
            }
            if (res.status) {
                fetchCurrencies();
                handleClose();
            }
        } catch (error) {
            console.error("Error saving currency:", error);
        }
    };

    const handleEdit = (item) => {
        setEditId(item._id);
        setFormData({
            name: item.name,
            country: item.country || '',
            code: item.code,
            symbol: item.symbol,
            status: item.status ? 'Active' : 'Inactive',
            isDefault: item.isDefault ?? false
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this currency?")) {
            try {
                const res = await currencyApi.deleteCurrency(id);
                if (res.status) {
                    fetchCurrencies();
                }
            } catch (error) {
                console.error("Error deleting currency:", error);
            }
        }
    };


    return (
        <>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        {/* <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                        <select
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            value={limit}
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPageIndex(0); // Reset to first page when limit changes
                            }}
                        >
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select> */}
                        <div className="navbar-search">
                            <input style={{ paddingRight: '40px' }} type="text"
                                className="bg-base h-40-px w-auto"
                                name="search"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </div>
                    </div>
                    {canAdd && (
                        <button
                            type="button"
                            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                            onClick={() => {
                                resetForm();
                                setIsModalOpen(true);
                            }}
                        >
                            <Icon
                                icon="ic:baseline-plus"
                                className="icon text-xl line-height-1"
                            />
                            Add Currency
                        </button>
                    )}
                </div>
                <div className="card-body p-24">
                    {isLoading ? (
                        <div className="text-center py-4">Loading...</div>
                    ) : (
                        <ReactTableComponent
                            data={currencies}
                            columns={columns}
                            pageIndex={pageIndex}
                            totalPages={Math.ceil(total / limit)}
                            onNextPage={handleNextPage}
                            onPreviousPage={handlePreviousPage}
                            totalRecords={total}
                        />
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)', }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content radius-16 bg-base">
                            <div className="modal-header py-16 px-24 border-bottom-0">
                                <h1 className="modal-title fs-5">
                                    {editId ? 'Edit Currency' : 'Add New Currency'}
                                </h1>
                                <button type="button" className="btn-close" onClick={handleClose} aria-label="Close" />
                            </div>
                            <div className="modal-body p-24">
                                <form className="row g-3" onSubmit={handleSubmit} noValidate>
                                    <div className="col-6">
                                        <label htmlFor="country" className="form-label fw-semibold text-primary-light text-sm mb-8">Country Name <span className="text-danger-600">*</span></label>
                                        <input type="text" className={`form-control radius-8 ${errors.country ? 'is-invalid' : ''}`} id="country" placeholder="Enter Country Name" value={formData.country} onChange={handleInputChange} />
                                        {errors.country && <div className="text-danger text-xs mt-1">{errors.country}</div>}
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="name" className="form-label fw-semibold text-primary-light text-sm mb-8">Currency Name <span className="text-danger-600">*</span></label>
                                        <input type="text" className={`form-control radius-8 ${errors.name ? 'is-invalid' : ''}`} id="name" placeholder="Enter Currency Name" value={formData.name} onChange={handleInputChange} />
                                        {errors.name && <div className="text-danger text-xs mt-1">{errors.name}</div>}
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="symbol" className="form-label fw-semibold text-primary-light text-sm mb-8">Symbol <span className="text-danger-600">*</span></label>
                                        <input type="text" className={`form-control radius-8 ${errors.symbol ? 'is-invalid' : ''}`} id="symbol" placeholder="e.g. $, ₹, £" value={formData.symbol} onChange={handleInputChange} />
                                        {errors.symbol && <div className="text-danger text-xs mt-1">{errors.symbol}</div>}
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="code" className="form-label fw-semibold text-primary-light text-sm mb-8">Code <span className="text-danger-600">*</span></label>
                                        <input type="text" className={`form-control radius-8 ${errors.code ? 'is-invalid' : ''}`} id="code" placeholder="e.g. USD, INR, GBP" value={formData.code} onChange={handleInputChange} />
                                        {errors.code && <div className="text-danger text-xs mt-1">{errors.code}</div>}
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="status" className="form-label fw-semibold text-primary-light text-sm mb-8">Status</label>
                                        <select className={`form-select radius-8 ${errors.status ? 'is-invalid' : ''}`} id="status" value={formData.status} onChange={handleInputChange} >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                        {errors.status && <div className="text-danger text-xs mt-1">{errors.status}</div>}
                                    </div>
                                    <div className="col-6" style={{ marginTop: '48px' }}>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="isDefault" checked={formData.isDefault} onChange={handleInputChange} />
                                            <label className="form-check-label fw-semibold text-primary-light text-sm" htmlFor="isDefault">
                                                Set as Default Currency
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-12 d-flex align-items-center justify-content-end gap-3 mt-24">
                                        <button type="button" className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-50 py-11 radius-8" onClick={handleClose}>Cancel</button>
                                        <button type="submit" className="btn btn-primary border border-primary-600 text-md px-50 py-12 radius-8">
                                            {editId ? 'Update' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CurrenciesLayer;